// ============================================================
// OAMATECH ERP PRO - Cliente HTTP (Axios)
// ============================================================
import axios from 'axios';
import toast from 'react-hot-toast';
import { numeroWhatsApp } from './instancia';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 15000,
});

// ── Interceptor de REQUEST: añade el token JWT automáticamente
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('oamatech_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Tienda activa seleccionada por el usuario (multitienda).
    // Se mira primero localStorage: asi la eleccion no se pierde al cerrar la pestana.
    const tiendaActiva = localStorage.getItem('oamatech_tienda_activa')
                      || sessionStorage.getItem('oamatech_tienda_activa');
    if (tiendaActiva) {
      config.headers['X-Tienda'] = tiendaActiva;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Interceptor de RESPONSE: manejo global de errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const mensaje = error.response?.data?.error || 'Error de conexión con el servidor';

    if (status === 401) {
      // Token expirado → limpiar sesión y redirigir al login
      sessionStorage.removeItem('oamatech_token');
      sessionStorage.removeItem('oamatech_usuario');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // silencioso: consultas de fondo (p. ej. rellenar un desplegable) que
    // pueden no estar permitidas a ese usuario; no hay que asustarle con un aviso
    if (status === 403 && error.config?.silencioso) return Promise.reject(error);
    // Copia de prueba: el login cerrado lo explica la pantalla de entrada; lo bloqueado, con su motivo
    if (status === 403 && error.response?.data?.prueba_terminada) return Promise.reject(error);
    if (status === 403 && error.response?.data?.prueba) { toast.error(mensaje); return Promise.reject(error); }
    if (status === 403) {
      toast.error('No tienes permisos para esta acción');
    } else if (status >= 500) {
      toast.error('Error del servidor. Inténtalo de nuevo.');
    }

    return Promise.reject(error);
  }
);

export default api;

// Cabeceras para las descargas que usan fetch directo (no pasan por el
// interceptor de axios). Sin la tienda activa, el backend busca en la tienda
// del token y devuelve 404 al trabajar en la otra tienda.
function cabecerasDescarga() {
  const token = sessionStorage.getItem('oamatech_token');
  const h = {};
  if (token) h.Authorization = `Bearer ${token}`;
  const tiendaActiva = localStorage.getItem('oamatech_tienda_activa')
                    || sessionStorage.getItem('oamatech_tienda_activa');
  if (tiendaActiva) h['X-Tienda'] = tiendaActiva;
  return h;
}

// Descarga un PDF desde la API
export async function descargarPDF(endpoint, filename) {
  const response = await fetch(`/api${endpoint}`, {
    headers: cabecerasDescarga()
  });
  if (!response.ok) {
    let msg = `Error ${response.status}`;
    try { const j = await response.json(); if (j.error) msg = j.error; } catch(e) {}
    throw new Error(msg);
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  // Abrir en pestaña nueva para VER el PDF
  const win = window.open(url, '_blank');
  // Si el navegador bloquea popups, descargar como fallback
  if (!win) {
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

// Abre un PDF en ventana nueva y lanza impresión automática
export async function imprimirPDF(endpoint) {
  const response = await fetch(`/api${endpoint}`, {
    headers: cabecerasDescarga()
  });
  if (!response.ok) {
    let msg = `Error ${response.status}`;
    try { const j = await response.json(); if (j.error) msg = j.error; } catch(e) {}
    throw new Error(msg);
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const ventana = window.open(url, '_blank');
  if (ventana) {
    ventana.onload = () => {
      setTimeout(() => {
        ventana.focus();
        ventana.print();
      }, 800);
    };
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

// Imprime DIRECTO via OamaPrint (servicio local puerto 9191)
// Modo servidor: el backend deja el ticket en la COLA de impresion
// y el OamaPrint de la tienda lo recoge e imprime en segundos.
export async function imprimirDirecto(endpointPDF, nombreImpresora = 'ticket') {
  const response = await fetch(`/api${endpointPDF}`, {
    method: 'POST',
    headers: cabecerasDescarga()
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Error al imprimir');
  }

  // Tanto si imprimio el propio backend (instalacion local) como si quedo
  // encolado para OamaPrint (modo servidor), el trabajo esta en marcha.
  return { ok: true };
}



// Edicion con candado de 5 minutos: si el backend pide PIN de autorizacion,
// se solicita al empleado y se reintenta.
export async function putConPin(url, payload) {
  try {
    return await api.put(url, payload);
  } catch (err) {
    if (err.response?.status === 403 && err.response?.data?.pin_requerido) {
      // El administrador no necesita PIN (no debería llegar aquí, pero por seguridad)
      let esAdmin = false;
      try { esAdmin = JSON.parse(sessionStorage.getItem('oamatech_usuario'))?.rol === 'admin'; } catch {}
      if (esAdmin) throw err;
      const pin = window.prompt('🔒 Esta ficha tiene más de 5 minutos.\nPide el PIN de autorización al administrador e introdúcelo:');
      if (!pin) throw err;
      return await api.put(url, { ...payload, pin_autorizacion: pin });
    }
    throw err;
  }
}

// ── Enviar un documento (ticket/factura/reporte) por WhatsApp ──
// Genera un enlace propio del documento. Antes se pegaba el token de sesion
// del empleado en la URL: el enlace dejaba de funcionar al caducar la sesion
// y ademas exponia una credencial del ERP a quien recibiera el mensaje.
// ventana: opcional, una pestaña abierta ya en el clic (así Safari no la bloquea
// aunque haya que esperar al servidor para crear el enlace).
export async function enviarDocumentoWhatsApp({ endpointPDF, telefono, mensaje, descripcion, ventana }) {
  const telFinal = numeroWhatsApp(telefono);
  if (!telFinal) return false;

  const base = (api.defaults.baseURL || '/api').replace(/\/api$/, '');
  const origen = base.startsWith('http') ? base : window.location.origin;

  let urlPDF;
  try {
    const { data } = await api.post('/documentos/enlace', {
      endpoint: endpointPDF, descripcion: descripcion || mensaje,
    });
    const sep = endpointPDF.includes('?') ? '&' : '?';
    urlPDF = `${origen}/api${endpointPDF}${sep}doc=${data.codigo}`;
  } catch (e) {
    // Si no se puede generar el enlace, se avisa en vez de mandar uno roto
    console.error('No se pudo crear el enlace del documento:', e?.response?.data || e);
    return false;
  }

  const texto = `${mensaje}\n\n📄 Documento: ${urlPDF}`;
  const destino = `https://wa.me/${telFinal}?text=${encodeURIComponent(texto)}`;
  if (ventana && !ventana.closed) ventana.location.href = destino;
  else window.open(destino, '_blank');
  return true;
}
